import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Phone, RefreshCw, Eye, EyeOff, Copy, Map } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CostEditForm } from "@/components/deliverers/CostEditForm";
import { MonthlyStatsTable } from "@/components/deliverers/MonthlyStatsTable";
import { DelivererRoutesTab } from "@/components/deliverers/DelivererRoutesTab";
import { DelivererAccessButtons } from "@/components/deliverers/DelivererAccessButtons";

const TABS = [
  { id: "stats", label: "Estatísticas" },
  { id: "rotas", label: "Rotas", icon: Map },
];

export function DelivererDetail({ delivererId, onBack }) {
  const { authAxios } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("stats");
  const [showCostEdit, setShowCostEdit] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [tokenVisible, setTokenVisible] = useState(false);
  const [fetchingToken, setFetchingToken] = useState(false);
  const [regeneratingToken, setRegeneratingToken] = useState(false);

  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;

  const fetchStats = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get(`/deliverers/${delivererId}/stats`);
      setStats(res.data);
    } catch {
      toast.error("Erro ao carregar estatisticas");
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delivererId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSaveCosts = async (costs) => {
    try {
      await authAxios.patch(`/deliverers/${delivererId}/costs`, costs);
      toast.success("Custos atualizados");
      setShowCostEdit(false);
      fetchStats();
    } catch {
      toast.error("Erro ao atualizar custos");
    }
  };

  const handleShowToken = async () => {
    setFetchingToken(true);
    try {
      const res = await authAxios.get(`/deliverers/${delivererId}/token`);
      setNewToken(res.data.token);
      setTokenVisible(true);
    } catch {
      toast.error("Erro ao carregar token");
    }
    setFetchingToken(false);
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm("Isto vai invalidar o token actual e desligar a sessão do estafeta. Continuar?")) return;
    setRegeneratingToken(true);
    try {
      const res = await authAxios.patch(`/deliverers/${delivererId}/regenerate-token`);
      setNewToken(res.data.token);
      setTokenVisible(true);
      toast.success("Token novo gerado. O estafeta terá de fazer login novamente.");
    } catch {
      toast.error("Erro ao regenerar token");
    }
    setRegeneratingToken(false);
  };

  const handleToggleActive = async () => {
    try {
      await authAxios.patch(`/deliverers/${delivererId}/toggle-active`);
      toast.success("Status alterado");
      fetchStats();
    } catch {
      toast.error("Erro");
    }
  };

  if (loading) return <LoadingScreen />;
  if (!stats) return null;

  const d = stats.deliverer;

  const allDays = new Set([
    ...Object.keys(stats.daily_orders || {}),
    ...Object.keys(stats.daily_distance || {})
  ]);
  const chartData = [...allDays]
    .sort()
    .slice(-30)
    .map(day => ({
      day: day.slice(5),
      pedidos: stats.daily_orders[day] || 0,
      km: parseFloat((stats.daily_distance[day] || 0).toFixed(1)),
    }));

  const costFields = [
    { label: "Custo/dia", value: d.cost_per_day, fmt: v => `€ ${v.toFixed(2)}` },
    { label: "Custo/hora", value: d.cost_per_hour, fmt: v => `€ ${v.toFixed(2)}` },
    { label: "Custo/pedido", value: d.cost_per_delivery, fmt: v => `€ ${v.toFixed(2)}` },
    { label: "Custo/km", value: d.cost_per_km, fmt: v => `€ ${v.toFixed(2)}` },
    { label: "% Frete", value: d.shipping_percent, fmt: v => `${v.toFixed(1)}%` },
    { label: "Min/pedido", value: d.min_per_delivery, fmt: v => `€ ${v.toFixed(2)}` },
    { label: "Min/dia", value: d.min_per_day, fmt: v => `€ ${v.toFixed(2)}` },
  ];

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="back-to-list" className="shrink-0">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold truncate">{d.name}</h1>
            <Badge variant={d.active ? "default" : "secondary"}>
              {d.active ? "Ativo" : "Inativo"}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{d.phone}</span>
            <span>{d.vehicle}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Dialog open={showCostEdit} onOpenChange={setShowCostEdit}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="edit-costs-btn">
                <Pencil className="w-4 h-4" /> Editar Custos
              </Button>
            </DialogTrigger>
            <DialogContent>
              <CostEditForm deliverer={d} onSave={handleSaveCosts} onClose={() => setShowCostEdit(false)} />
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={handleToggleActive} data-testid="toggle-active-btn">
            {d.active ? "Desativar" : "Ativar"}
          </Button>
        </div>
      </div>

      {/* Cost Summary Bar */}
      <Card data-testid="cost-summary">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
            {costFields.map(f => (
              <div key={f.label} className="text-center">
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{f.label}</p>
                <p className="text-lg font-bold mt-0.5">
                  {f.value ? f.fmt(f.value) : <span className="text-sm font-normal text-muted-foreground">Não configurado</span>}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Token Row */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground shrink-0">Token:</span>
        {newToken ? (
          <>
            <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono truncate max-w-[260px]" data-testid="new-token-value">
              {tokenVisible ? newToken : `${newToken.slice(0, 8)}•••••${newToken.slice(-4)}`}
            </code>
            <button onClick={() => setTokenVisible(v => !v)} className="p-1 hover:bg-gray-100 rounded" data-testid="toggle-token-visibility">
              {tokenVisible ? <EyeOff className="w-3.5 h-3.5 text-muted-foreground" /> : <Eye className="w-3.5 h-3.5 text-muted-foreground" />}
            </button>
            <button onClick={() => { navigator.clipboard.writeText(newToken); toast.success("Token copiado"); }} className="p-1 hover:bg-gray-100 rounded" data-testid="copy-token-btn">
              <Copy className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </>
        ) : (
          <span className="text-xs text-muted-foreground italic" data-testid="token-hidden-msg">Token oculto por segurança.</span>
        )}
        {!newToken && (
          <Button variant="outline" size="sm" className="h-7 px-2 text-xs gap-1" onClick={handleShowToken} disabled={fetchingToken} data-testid="show-token-btn">
            <RefreshCw className={`w-3 h-3 ${fetchingToken ? "animate-spin" : ""}`} />
            Ver token
          </Button>
        )}
        {newToken && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1 text-destructive hover:text-destructive" onClick={handleRegenerateToken} disabled={regeneratingToken} data-testid="regen-token-btn">
            <RefreshCw className={`w-3 h-3 ${regeneratingToken ? "animate-spin" : ""}`} />
            Invalidar e gerar novo
          </Button>
        )}
        <DelivererAccessButtons delivererId={delivererId} phone={d.phone} />
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.id
                ? "border-emerald-600 text-emerald-700"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.icon && <t.icon className="w-4 h-4" />}
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "stats" && (
        <>
          {/* Monthly Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MonthlyStatsTable title="Mês Anterior" stats={stats.last_month} />
            <MonthlyStatsTable title="Mês Atual" stats={stats.current_month} />
          </div>

          {/* Charts */}
          {chartData.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card data-testid="orders-chart">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-3">Pedidos por Dia</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                      <Line type="monotone" dataKey="pedidos" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card data-testid="distance-chart">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-3">Distância Percorrida por Dia</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                      <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" unit=" km" />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                      <Line type="monotone" dataKey="km" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {activeTab === "rotas" && <DelivererRoutesTab delivererId={delivererId} />}
    </div>
  );
}
